import { handleMicrosoftLoginResponse } from "@components/login/microsoft/LoginMicrosoft"
import { RequestStatus } from "@redux/app/RequestStatus"
import { useAppDispatch, useAppSelector } from "@redux/app/hooks"
import { setWarningMessage, updateIsConnected } from "@redux/features/appState/appStateSlice"
import {
	fetchUserData,
	getUserData,
	getUserDataStatus,
} from "@redux/features/userData/userDataSlice"
import { useQueryClient } from "@tanstack/react-query"
import { Navigate, Outlet } from "@tanstack/react-router"
import "@ui-lib/core/chartJS/chartSetup"
import { LoadingCircle } from "@ui-lib/src"
import { common, scanClient } from "@utils/common"
import { requestApi2 } from "@utils/http"
import { getUserWebSocket } from "@utils/websocket/getUserWebSocket"
import { portalSocket, portalSocketEvents } from "@utils/websocket/portalSocket"
import { endpoints, messageTypes } from "api"
import { EventSourceManager } from "open-lib"
import { useEffect, useState } from "react"
import "regenerator-runtime/runtime"
import { getAppState } from "./app/store"
import { APP_STRUCTURE_QUERY_KEY } from "./features/appStructure/queries"
import { useAppStructure } from "./features/appStructure/useAppStructure"
import { setCurrentCompany } from "./features/currentCompany/currentCompanySlice"
import {
	fetchStateAliases,
	getStateAliases,
	getStateAliasesStatus,
} from "./features/stateAliases/stateAliasesSlice"
import { pca } from "./index"

export default function App() {
	const [companyName, setCompanyName] = useState<string>()
	const [isSignedIn, setIsSignedIn] = useState(false)

	const queryClient = useQueryClient()

	const dispatch = useAppDispatch()

	const userData = useAppSelector(getUserData)
	const userDataStatus = useAppSelector(getUserDataStatus)

	const {
		data: appStruct,
		isError: appStructureIsError,
		isPending: appStructureIsPending,
	} = useAppStructure()

	const stateAliases = useAppSelector(getStateAliases)

	const appState = useAppSelector(getAppState)

	const wsClient = getUserWebSocket()

	useEffect(() => {
		pca.handleRedirectPromise()
			.then((response) => {
				if (response !== null) {
					handleMicrosoftLoginResponse(response, dispatch)
				}
			})
			.catch((error) => {
				console.error("Redirect error: ", error)
			})
	}, [])

	const signIn = async () => {
		const key = await requestApi2(endpoints.getSignInKey)
		if (key !== null) {
			wsClient.send(messageTypes.signIn, key)
			setIsSignedIn(true)
		}
	}

	useEffect(() => {
		const onConnected = () => dispatch(updateIsConnected(true))
		const onDisconnected = () => dispatch(updateIsConnected(false))
		portalSocket.onConnected.addListener(onConnected)
		portalSocket.onDisconnected.addListener(onDisconnected)
		const eventManager = new EventSourceManager()
		eventManager.bind(portalSocketEvents.onWebPortalWarning, (event) => {
			dispatch(setWarningMessage(event.message))
		})
		return () => {
			portalSocket.onConnected.removeListener(onConnected)
			portalSocket.onDisconnected.removeListener(onDisconnected)
			eventManager.clear()
		}
	}, [])

	useEffect(() => {
		if (
			isSignedIn &&
			(!appState.isConnected ||
				userData.isAuthenticated === undefined ||
				userData.isAuthenticated === false)
		) {
			setIsSignedIn(false)
		}
		if (appState.isConnected && userData.isAuthenticated === true && !isSignedIn) {
			signIn()
		}
	}, [appState.isConnected, userData.isAuthenticated])

	useEffect(() => {
		if (userData.isAuthenticated === true) {
			portalSocket.setUserIsSignedIn()
		}
	}, [userData.isAuthenticated])

	useEffect(() => {
		if (userDataStatus === RequestStatus.idle) {
			dispatch(fetchUserData())
		}
	}, [userDataStatus])

	const stateAliasesStatus = useAppSelector(getStateAliasesStatus)

	useEffect(() => {
		const refetch = async () => {
			await queryClient.refetchQueries({
				queryKey: APP_STRUCTURE_QUERY_KEY,
			})
		}
		if (userData.isAuthenticated === true) {
			if (appStruct == null) {
				refetch()
				setCompanyName(undefined)
				dispatch(setCurrentCompany(undefined))
			}
			if (
				stateAliases === null &&
				(stateAliasesStatus === RequestStatus.idle ||
					stateAliasesStatus === RequestStatus.failed)
			) {
				dispatch(fetchStateAliases())
			}
		}
	}, [userData])

	useEffect(() => {
		if (appStructureIsError !== true && appStructureIsPending !== true) {
			console.log("😎 ~ appStruct", appStruct)
			common.sensorsById.clear()
			if (appStruct == null) {
				return
			}
			setCompanyName(Object.keys(appStruct)[0])
			for (const client of Object.keys(appStruct)) {
				const clientRef = appStruct[client]
				if (clientRef === undefined) {
					continue
				}
				scanClient(clientRef, client, client)
			}
		}
	}, [appStruct, dispatch])

	useEffect(() => {
		if (appStruct == null) {
			return
		}
		if (companyName === undefined || Object.keys(appStruct)[0] !== companyName) {
			setCompanyName(Object.keys(appStruct)[0])
			return
		}
	}, [appStruct, companyName])

	if (userData.isAuthenticated === false) {
		return (
			<>
				<Outlet />
				<Navigate to={"/login"} />
			</>
		)
	}

	return appStruct != null &&
		companyName !== undefined &&
		Object.keys(appStruct)[0] === companyName ? (
		<Outlet />
	) : (
		<LoadingCircle containerHeightWithUnit="100vh" />
	)
}
